import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ErrorService } from "@/lib/errors";
import { useCabinetId } from "@/hooks/useCabinetMemberMe";
import {
  fetchActPersonalizationSource,
  saveRedactionStyle,
} from "@/services/actPersonalizationApi";

/**
 * Cache prefix shared between the read query and the post-save invalidation sweep.
 * Exported so `sessionDoneBridge` can invalidate after a caseless-dock turn,
 * following the same pattern as `MEMORY_SETTINGS_KEY`, `PIECE_TITLE_PROMPT_KEY`,
 * and `VOICE_TRANSCRIPTION_SETTINGS_KEY`.
 */
export const REDACTION_STYLE_KEY = "redaction-style";

/**
 * Cabinet-scoped query key for the redaction style.
 * Mirrors `memorySettingsKey`, `pieceTitlePromptKey`, `voiceTranscriptionSettingsKey`.
 */
export const redactionStyleKey = (cabinetId: string): [string, string] =>
  [REDACTION_STYLE_KEY, cabinetId];

export interface UseRedactionStyleResult {
  /** Current Style.md text (empty string when the cabinet never configured one). */
  content: string;
  /** True while the initial fetch is in flight. */
  isLoading: boolean;
  /** Persist a new Style.md text; resolves once the PATCH succeeds. */
  save: (next: string) => Promise<void>;
  /** True while a save is in flight — use to disable the Enregistrer button. */
  isSaving: boolean;
}

/**
 * Read + save the cabinet's "Style.md" (the `redaction_style` block) — the
 * hand-edited plume that steers act generation.
 *
 * Reuses the existing act-personalization service layer instead of new endpoints:
 *   - {@link fetchActPersonalizationSource} exposes `redactionStyle.content`
 *     (a `RedactionStyleBlock | null`; `null` = never configured → "").
 *   - {@link saveRedactionStyle} PATCHes the new markdown.
 *
 * Style.md is now a plain hand-edited text block, edited with the same UX as the
 * user "Préférences de comportement" memory block — the former auto-enrichment /
 * revert / analyze flow is intentionally NOT wired here.
 *
 * The query is cabinet-scoped (`['redaction-style', cabinetId]`, enabled only
 * once `cabinetId` resolves) even though the source endpoint derives the cabinet
 * server-side from the JWT: keying by cabinet keeps the cache correct across a
 * cabinet swap (app-wide convention).
 */
export function useRedactionStyle(): UseRedactionStyleResult {
  const cabinetId = useCabinetId();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: redactionStyleKey(cabinetId ?? ""),
    enabled: !!cabinetId,
    queryFn: async (): Promise<string> => {
      const source = await fetchActPersonalizationSource();
      return source.redactionStyle?.content ?? "";
    },
  });

  const mutation = useMutation({
    mutationFn: (next: string) => saveRedactionStyle(next),
    onSuccess: () => {
      toast.success("Style mis à jour.");
      // Invalidate the cabinet-specific entry AND the general key — a consumer
      // that only knows the swap-agnostic key still gets refreshed.
      if (cabinetId) {
        queryClient.invalidateQueries({ queryKey: redactionStyleKey(cabinetId) });
      }
      queryClient.invalidateQueries({ queryKey: [REDACTION_STYLE_KEY] });
    },
    onError: (error: unknown) => {
      ErrorService.handle(
        error instanceof Error ? error : new Error("Impossible d'enregistrer le Style.md"),
        {
          userMessage: "Le Style.md n'a pas pu être enregistré. Veuillez réessayer.",
          context: { action: "save_redaction_style" },
        },
      );
    },
  });

  return {
    content: query.data ?? "",
    isLoading: query.isLoading,
    // Unwrap mutateAsync to a `Promise<void>` so callers just `await save(next)`.
    save: async (next: string) => {
      await mutation.mutateAsync(next);
    },
    isSaving: mutation.isPending,
  };
}
